import { Injectable } from '@angular/core';
import { AuthService } from '@max/auth/services/auth.service';
import { BusinessTransactionType } from '../../../foundation/models/enums/business-transaction-type.enum';
import { ISourceSystemType } from '../../../foundation/models/enums/source-system-type.enum';
import { TabObject } from '../../components/tabs/tabs.model';
import { IRoute } from '../../models/iroute.interface';
import { RouteBuilderService } from './route-builder.service';

@Injectable()
/**
 * Builds transaction routes for driver
 */
export class DriverRouteBuilderService extends RouteBuilderService {
    private businessTransactionType: BusinessTransactionType;

    constructor(private authService: AuthService) {
        super();
        this.businessTransactionType = new BusinessTransactionType();
    }

    public get sourceSystemType(): ISourceSystemType {
        return this.sourceSystemTypes.Driver;
    }

    /**
     * Gets the route list for the transaction type provided.
     * @returns: Array of routes for transaction.
     */
    getRouteList(businessTransactionTypeId: number): any[] {
        const routeList: IRoute[] = [];

        if (this.businessTransactionType.CredentialIssuance.id == businessTransactionTypeId) {
            return this.getCredentialIssuanceRoutes();
        } else if (this.businessTransactionType.CredentialRenewal.id == businessTransactionTypeId) {
            return this.getCredentialRenewalRoutes();
        } else if (this.businessTransactionType.CredentialDuplicate.id == businessTransactionTypeId) {
            return this.getCredentialDuplicateRoutes();
        } else if (this.businessTransactionType.StandAloneKnowledgeTest.id == businessTransactionTypeId) {
            return this.getStandAloneKnowledgeRoutes();
        } else if (this.businessTransactionType.StandAloneMotorcycleSkillTest.id == businessTransactionTypeId) {
            return this.getStandAloneMotorcycleSkillRoutes();
        } else if (this.businessTransactionType.StandAloneNonCommercialSkillTest.id == businessTransactionTypeId) {
            return this.getStandAloneNonCommercialSkillRoutes();
        } else if (this.businessTransactionType.StandAloneCommercialSkillTest.id == businessTransactionTypeId) {
            return this.getStandAloneCommercialSkillsRoutes();
        } else if (this.businessTransactionType.StandAloneVisionScreening.id == businessTransactionTypeId) {
            return this.getStandAloneVisionScreeningRoutes();
        } else if (this.businessTransactionType.DriverMVR.id == businessTransactionTypeId) {
            return this.getDriverMVRRoutes();
        } else if (this.businessTransactionType.CancelCredential.id == businessTransactionTypeId) {
            return this.getcancelCredentialRoutes();
        } else if (this.businessTransactionType.StandAloneVoterRegistration.id == businessTransactionTypeId) {
            return this.getStandAloneVoterRRegistrationRoutes();
        }

        return routeList;
    }

    /**
     * Gets the route list for credential issuance
     * @returns: Array of routes for Credential Issuance.
     */
    private getCredentialRenewalRoutes(): TabObject[] {
        // let transactionService: TransactionService = this.injector.get(TransactionService);
        // let vehicleMediatorService: VehicleMediatorService = this.injector.get(VehicleMediatorService);

        return this.getCredentialIssuanceRoutes();
    }

    /**
     * Gets the route list for credential issuance
     * @returns: Array of routes for Credential Issuance.
     */
    private getCredentialDuplicateRoutes(): TabObject[] {
        return this.getCredentialIssuanceRoutes();
    }

    /**
     * Gets the route list for credential issuance
     * @returns: Array of routes for Credential Issuance.
     */
    private getCredentialIssuanceRoutes(): TabObject[] {
        const commands = [
            { id: 1, icon: 'print' }
        ];

        let hasTransactionHistoryAccess = this.authService.hasACSTransactionClaimEx(this.authService.acsTransactionClaim, 'CDLServiceTransferInformation');
        if (hasTransactionHistoryAccess) {
            commands.push({ id: 2, icon: 'history'});
        }

        return [
            { name: 'issuance-customer', title: 'Customer', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: commands, helpContentUrl: '#CONTENT/Driver/Credential_Issuance.htm%3FTocPath%3DDriver%7C_____1' },
            { name: 'credentialdetails', title: 'Credential', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: commands, helpContentUrl: '#CONTENT/Driver/Credential_Issuance.htm%3FTocPath%3DDriver%7C_____1' },
            { name: 'questionnaire', title: 'Questionnaire', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: commands, helpContentUrl: '#CONTENT/Driver/Credential_Issuance.htm%3FTocPath%3DDriver%7C_____1' },
            { name: 'requirement', title: 'Requirements', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: true, commands: commands, helpContentUrl: '#CONTENT/Driver/Credential_Issuance.htm%3FTocPath%3DDriver%7C_____1' },
            { name: 'basic-document', title: 'Document', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: commands}
        ];
    }

    private getStandAloneKnowledgeRoutes(): TabObject[] {
        return [
            { name: 'customer-detail', title: 'Customer', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: [], helpContentUrl: '#CONTENT/Driver/Knowledge_Only_Test.htm%3FTocPath%3DDriver|_____16' },
            { name: 'knowledge-test', title: 'Knowledge Test', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: [], helpContentUrl: '#CONTENT/Driver/Knowledge_Only_Test.htm%3FTocPath%3DDriver|_____16' }
        ];
    }

    private getStandAloneMotorcycleSkillRoutes(): TabObject[] {
        return [
            { name: 'customer-detail', title: 'Customer', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: [], helpContentUrl: '#CONTENT/Driver/Motorcycle_Skills_Test.htm%3FTocPath%3DDriver|_____20' },
            { name: 'motorcycle-skill-test', title: 'Motorcycle Skill Test', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: [], helpContentUrl: '#CONTENT/Driver/Motorcycle_Skills_Test.htm%3FTocPath%3DDriver|_____20' }
        ];
    }

    private getStandAloneCommercialSkillsRoutes(): TabObject[] {
        return [
            { name: 'customer-detail', title: 'Customer', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: [] },
            { name: 'commercial-skill-test', title: 'Commercial Skill Test', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: [] }
        ];
    }

    private getStandAloneNonCommercialSkillRoutes(): TabObject[] {
        return [
            { name: 'customer-detail', title: 'Customer', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: [], helpContentUrl: '#CONTENT/Driver/Non-Commercial_Skills_Test.htm%3FTocPath%3DDriver|_____21' },
            { name: 'non-commercial-skill-test', title: 'Non Commercial Skill Test', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: [], helpContentUrl: '#CONTENT/Driver/Non-Commercial_Skills_Test.htm%3FTocPath%3DDriver|_____21' }
        ];
    }

    private getStandAloneVisionScreeningRoutes(): TabObject[] {
        return [
            { name: 'customer-detail', title: 'Customer', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: [], helpContentUrl: '#CONTENT/Driver/Vision_Only_Test.htm%3FTocPath%3DDriver|_____24' },
            { name: 'vision-screening', title: 'Vision Screening', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: [], helpContentUrl: '#CONTENT/Driver/Vision_Only_Test.htm%3FTocPath%3DDriver|_____24' }
        ];
    }

    private getcancelCredentialRoutes(): TabObject[] {
        return [
            { name: 'credential-cancellation', title: 'Credential', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: [], helpContentUrl: '#CONTENT/Driver/Cancel_Credential.htm%3FTocPath%3DDriver|_____1' },
            { name: 'credential-cancellation-document', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: []}
        ];
    }

    private getDriverMVRRoutes(): TabObject[] {
        return [
            { name: 'driver-mvr', title: 'Driver MVR', isActive: true, isHidden: false, route: null, hasTooltip: false, hasValidation: false, helpContentUrl: '#CONTENT/Driver/Driver_MVR.htm%3FTocPath%3DDriver|_____14' },
            { name: 'document-mvr', title: 'Documents', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false }
        ];
    }

    private getStandAloneVoterRRegistrationRoutes(): TabObject[] {
        return [
            { name: 'voter-registration', title: 'Questionnaire', isActive: false, isHidden: false, route: null, hasTooltip: false, hasValidation: false, commands: [] }
        ];
    }
}
